/**
 * Inventory Exceptions — juice stock WMS holds that TraceGains cannot vouch for.
 *
 * Four panels, each one exception class, in the order a reader should work them:
 * the three items that need an XREF (a fix in one place clears hundreds of
 * loads), then the lots whose certificates disagree with each other, then the
 * lots with no certificate at all.
 *
 * Every count comes from the server. The conflicts table drills into
 * `conflict_records`, which is the individual certificates behind a lot — the
 * only place in this report where a lot appears more than once, and the reason
 * gallons are never summed there (a lot with three COAs would read as three
 * times the inventory). The panel shows the loads/gallons figure the server
 * computed from the load rows instead.
 */

import { Fragment, useState } from 'react'

import type { CustomReportProps } from '..'
import { Card, Empty } from '../shared/Card'
import { displayText, formatDate } from '../shared/format'
import { count, dataset, type Keyed, text } from '../shared/rows'

import '../report.css'

const NO_RECORD = 'NO_TG_RECORD'

/** Gallons, to the nearest whole one. The source records tenths on some loads. */
function gallons(value: unknown): string {
  const n = typeof value === 'number' ? value : Number(value ?? 0)
  if (!Number.isFinite(n)) return '—'
  return n.toLocaleString(undefined, { maximumFractionDigits: 0 })
}

/**
 * A measured value as recorded. Brix runs to two decimals and acid to three, so
 * a fixed format would pad one or truncate the other; trailing zeros are trimmed
 * the same way the Specification Exceptions report trims them.
 */
function measure(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return '—'
  return String(Number(n.toFixed(4)))
}

function Chevron({ open }: { open: boolean }) {
  return (
    <span className={`chevron${open ? ' is-open' : ''}`} aria-hidden="true">
      <svg width="10" height="10" viewBox="0 0 10 10">
        <path d="M3 1 L7 5 L3 9" fill="none" stroke="currentColor" strokeWidth="1.6" />
      </svg>
    </span>
  )
}

export function InventoryExceptions({ result }: CustomReportProps) {
  const summary = dataset(result, 'summary')
  const xref = dataset(result, 'xref')
  const conflicts = dataset(result, 'conflicts')
  const conflictRecords = dataset(result, 'conflict_records')
  const unmatched = dataset(result, 'unmatched')

  const totalLoads = summary.reduce((sum, row) => sum + count(row.loads), 0)
  const totalGallons = summary.reduce((sum, row) => sum + count(row.gallons), 0)

  return (
    <div className="bespoke-report">
      <p className="report-summary">
        {totalLoads.toLocaleString()} {totalLoads === 1 ? 'load' : 'loads'} carrying{' '}
        {gallons(totalGallons)} gallons sit in one or more exception below. A load can appear in
        more than one, so the four figures do not sum to a total.
      </p>

      <Card
        title="Exceptions"
        subtitle="Each row counts the loads in that exception. The classes overlap — an item needing an XREF often also has conflicting certificates."
      >
        {summary.length === 0 ? (
          <Empty label="No exceptions for these filters." />
        ) : (
          <div className="ie-tally">
            {summary.map((row) => (
              <div className="ie-tally-cell" key={displayText(text(row.exception))}>
                <span className="ie-tally-label">{displayText(text(row.exception))}</span>
                <span className="ie-tally-value">{gallons(row.gallons)}</span>
                <span className="ie-tally-unit">gallons</span>
                <span className="ie-tally-detail">
                  {count(row.lots).toLocaleString()} {count(row.lots) === 1 ? 'lot' : 'lots'} ·{' '}
                  {count(row.loads).toLocaleString()} {count(row.loads) === 1 ? 'load' : 'loads'} ·{' '}
                  {gallons(row.gallons_available)} available
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card
        title="Items Requiring XREF"
        subtitle="WMS files these under one item code and TraceGains under another. The alias is what lets the match find them; without it every load of these items reads as having no certificate."
      >
        {xref.length === 0 ? (
          <Empty label="No item aliases are recorded." />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="detail-table ie-xref-table">
              <thead>
                <tr>
                  <th>WMS SKU</th>
                  <th>TraceGains item</th>
                  <th>Description</th>
                  <th className="cell-number">Lots</th>
                  <th className="cell-number">Loads</th>
                  <th className="cell-number">Gallons</th>
                </tr>
              </thead>
              <tbody>
                {xref.map((row) => (
                  <tr key={displayText(text(row.sku))} className="detail-row">
                    <td className="cell-mono">{displayText(text(row.sku))}</td>
                    <td className="cell-mono">{displayText(text(row.alias_tg_item))}</td>
                    <td className="cell-truncate" title={displayText(text(row.skudesc))}>
                      {displayText(text(row.skudesc))}
                    </td>
                    <td className="cell-number">{count(row.lots).toLocaleString()}</td>
                    <td className="cell-number">{count(row.loads).toLocaleString()}</td>
                    <td className="cell-number">{gallons(row.gallons)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <ConflictTable rows={conflicts} records={conflictRecords} />

      <Card
        title="Lots with no TraceGains record"
        subtitle="Nothing to check this stock against. A lot whose number matched a record belonging to a different item is marked — lot numbers are not unique on their own, so that match is a coincidence, not a certificate."
      >
        {unmatched.length === 0 ? (
          <Empty label="Every lot in scope has a TraceGains record." />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="detail-table ie-unmatched-table">
              <thead>
                <tr>
                  <th>Lot</th>
                  <th>SKU</th>
                  <th>Description</th>
                  <th>Plant</th>
                  <th>Match</th>
                  <th className="cell-number">Loads</th>
                  <th className="cell-number">Gallons</th>
                  <th className="cell-number">Available</th>
                  <th>Oldest receipt</th>
                </tr>
              </thead>
              <tbody>
                {unmatched.map((row, index) => {
                  const match = text(row.match_result)
                  return (
                    <tr
                      key={`${displayText(text(row.lot))}-${displayText(text(row.sku))}-${String(
                        index,
                      )}`}
                      className="detail-row"
                    >
                      <td className="cell-mono">{displayText(text(row.lot))}</td>
                      <td className="cell-mono">{displayText(text(row.sku))}</td>
                      <td className="cell-truncate" title={displayText(text(row.skudesc))}>
                        {displayText(text(row.skudesc))}
                      </td>
                      <td>{displayText(text(row.plant))}</td>
                      <td>
                        {match === NO_RECORD ? (
                          <span className="ie-badge">no record</span>
                        ) : (
                          <span
                            className="ie-badge is-warning"
                            title="The lot number matched a TraceGains record belonging to a different item."
                          >
                            item disagrees
                          </span>
                        )}
                      </td>
                      <td className="cell-number">{count(row.loads).toLocaleString()}</td>
                      <td className="cell-number">{gallons(row.gallons)}</td>
                      <td className="cell-number">{gallons(row.gallons_available)}</td>
                      <td>{formatDate(text(row.oldest_receipt))}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}

function ConflictTable({ rows, records }: { rows: Keyed[]; records: Keyed[] }) {
  const [open, setOpen] = useState<Set<string>>(new Set())

  const toggle = (key: string) => {
    const next = new Set(open)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    setOpen(next)
  }

  const disagreeing = rows.filter((row) => count(row.verdicts) > 1).length

  return (
    <Card
      title="Lots with more than one TraceGains record"
      subtitle={
        `${rows.length.toLocaleString()} ${rows.length === 1 ? 'lot carries' : 'lots carry'} ` +
        `several certificates, and ${disagreeing.toLocaleString()} of them disagree about the ` +
        'verdict. Open a lot to see each certificate.'
      }
    >
      {rows.length === 0 ? (
        <Empty label="Every lot in scope has at most one TraceGains record." />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="detail-table ie-conflict-table">
            <thead>
              <tr>
                <th aria-label="Expand" />
                <th>Lot</th>
                <th>SKU</th>
                <th>Description</th>
                <th>Source</th>
                <th className="cell-number">Records</th>
                <th className="cell-number">POs</th>
                <th>Statuses</th>
                <th>Dispositions</th>
                <th className="cell-number">Loads</th>
                <th className="cell-number">Gallons</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => {
                const lot = displayText(text(row.lot))
                const sku = displayText(text(row.sku))
                const key = `${lot}-${sku}-${String(index)}`
                const isOpen = open.has(key)
                const split = count(row.verdicts) > 1
                const behind = records.filter(
                  (record) => text(record.lot) === text(row.lot) && text(record.sku) === text(row.sku),
                )
                return (
                  <Fragment key={key}>
                    <tr
                      className={`detail-row ie-conflict-row${isOpen ? ' is-open' : ''}`}
                      onClick={() => {
                        toggle(key)
                      }}
                    >
                      <td className="chevron-cell">
                        <button
                          type="button"
                          className="link-button"
                          aria-expanded={isOpen}
                          aria-label={`${isOpen ? 'Collapse' : 'Expand'} lot ${lot}`}
                          onClick={(event) => {
                            event.stopPropagation()
                            toggle(key)
                          }}
                        >
                          <Chevron open={isOpen} />
                        </button>
                      </td>
                      <td className="cell-mono">{lot}</td>
                      <td className="cell-mono">{sku}</td>
                      <td className="cell-truncate" title={displayText(text(row.skudesc))}>
                        {displayText(text(row.skudesc))}
                      </td>
                      <td>{displayText(text(row.tg_source))}</td>
                      <td className="cell-number">{count(row.records).toLocaleString()}</td>
                      <td className="cell-number">{count(row.pos).toLocaleString()}</td>
                      <td className={split ? 'ie-split' : undefined}>
                        {displayText(text(row.statuses))}
                      </td>
                      <td className={split ? 'ie-split' : undefined}>
                        {displayText(text(row.dispositions))}
                      </td>
                      <td className="cell-number">{count(row.loads).toLocaleString()}</td>
                      <td className="cell-number">{gallons(row.gallons)}</td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td className="expanded-cell" colSpan={11}>
                          {behind.length === 0 ? (
                            <Empty label="No certificates behind this lot." />
                          ) : (
                            <table className="detail-table ie-record-table">
                              <thead>
                                <tr>
                                  <th className="cell-number">TG ID</th>
                                  <th>PO</th>
                                  <th className="cell-number">Rank</th>
                                  <th>Status</th>
                                  <th>Disposition</th>
                                  <th className="cell-number">Brix</th>
                                  <th className="cell-number">Acid</th>
                                  <th className="cell-number">Brix/acid</th>
                                  <th>Measured</th>
                                  <th>Soft deleted</th>
                                </tr>
                              </thead>
                              <tbody>
                                {behind.map((record) => (
                                  <tr key={String(record.tg_id)} className="detail-row">
                                    <td className="cell-number cell-mono">
                                      {displayText(text(record.tg_id))}
                                    </td>
                                    <td className="cell-mono">
                                      {displayText(text(record.po_number))}
                                    </td>
                                    <td className="cell-number">
                                      {displayText(text(record.coa_rank))}
                                    </td>
                                    <td>{displayText(text(record.spec_status))}</td>
                                    <td>{displayText(text(record.disposition))}</td>
                                    <td className="cell-number">{measure(record.brix)}</td>
                                    <td className="cell-number">{measure(record.acid_citric)}</td>
                                    <td className="cell-number">
                                      {measure(record.brix_acid_ratio)}
                                    </td>
                                    <td>{formatDate(text(record.measured_at))}</td>
                                    <td>
                                      {text(record.coa_is_deleted) === 'true' ? (
                                        <span
                                          className="ie-badge is-warning"
                                          title="Flagged deleted in TraceGains. The flag is unreliable, so the record is shown."
                                        >
                                          deleted
                                        </span>
                                      ) : (
                                        ''
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}
